import { isBracketemDetails } from "model/PickemDetails/Bracketem/Details";
import {
  PickemDetails,
  PICKEM_OPEN_THRESHOLD_MS,
} from "model/PickemDetails/PickemDetails";
import { TournamentPickemDetails } from "model/PickemDetails/TournamentPickemDetails";
import {
  Bracket,
  getBracketMatchIds,
} from "model/Tournament/Segment/Bracket/Bracket";
import { getRoundMatchIds } from "model/Tournament/Segment/RoundsSegmentGroup";
import {
  getSegmentGroupMatchIds,
  getSegmentMatchIds,
  TournamentSegment,
} from "model/Tournament/Segment/TournamentSegment";
import { RootState } from "app/store";
import { getTimeMs, numberInRange } from "TimeHelper";
import { nonUndefined } from "Utility";
import { selectMatchStartTime } from "./Match";
import { selectTournamentsByYear, selectTournamentSegment } from "./Tournament";
import { Swiss } from "model/Tournament/Segment/Swiss/Swiss";

export const selectTournamentPickemDetails = (
  state: RootState,
  tournamentId: string
): TournamentPickemDetails | undefined => {
  return state.pickemDetails.pickemDetails[tournamentId];
};

export const selectTournamentHasPickemDetails = (
  state: RootState,
  tournamentId: string
) => {
  return selectTournamentPickemDetails(state, tournamentId) !== undefined;
};

export const selectAllTournamentPickemDetails = (state: RootState) => {
  return Object.values(state.pickemDetails.pickemDetails);
};

export const selectAllPickemDetails = (state: RootState) => {
  return selectAllTournamentPickemDetails(state).flatMap((t) => t.pickems);
};

export const selectPickemDetails = (
  state: RootState,
  tournamentId: string | undefined,
  pickemId: string | undefined
) => {
  if (!tournamentId || !pickemId) {
    return undefined;
  }

  const tournamentPickemDetails = selectTournamentPickemDetails(
    state,
    tournamentId
  );
  if (!tournamentPickemDetails) {
    return undefined;
  }

  return tournamentPickemDetails.pickems.find((p) => p.pickemId === pickemId);
};

const selectPickemMatchIds = (
  state: RootState,
  matchIds: string[],
  details: PickemDetails
) => {
  if (!!details.earliestMatchTimeMs || !!details.latestMatchTimeMs) {
    matchIds = matchIds.filter((id) => {
      const matchStartTime = selectMatchStartTime(state, id);
      if (!matchStartTime) {
        return false;
      }

      return (
        (!!details.earliestMatchTimeMs
          ? matchStartTime >= details.earliestMatchTimeMs
          : true) &&
        (!!details.latestMatchTimeMs
          ? matchStartTime <= details.latestMatchTimeMs
          : true)
      );
    });
  }

  return matchIds;
};

export const selectPickemMatchIdsFromSegment = (
  state: RootState,
  segment: TournamentSegment,
  details: PickemDetails
) => {
  var matchIds: string[] = [];
  if (isBracketemDetails(details)) {
    matchIds = !!details.groupId
      ? getSegmentGroupMatchIds(segment, details.groupId) ?? []
      : getSegmentMatchIds(segment) ?? [];
  } else {
    if (!!details.matchIds) {
      return details.matchIds;
    }

    const subSegmentNum = details.subSegmentNum;
    if (
      subSegmentNum !== undefined &&
      (segment.type === "Season" || segment.type === "Swiss")
    ) {
      matchIds = segment.groups
        .filter((g) => !details.groupId || g.groupId === details.groupId)
        .flatMap((g) => getRoundMatchIds(g.rounds, subSegmentNum) ?? []);
    } else {
      matchIds = getSegmentMatchIds(segment) ?? [];
    }

    if (!!details.ignoreMatchIds) {
      matchIds = matchIds?.filter(
        (id) => !details.ignoreMatchIds!.includes(id)
      );
    }
  }

  return selectPickemMatchIds(state, matchIds, details);
};

export const selectPickemMatchIdsFromBracket = (
  state: RootState,
  bracket: Bracket,
  details: PickemDetails
) => {
  const matchIds = getBracketMatchIds(bracket);
  return selectPickemMatchIds(state, matchIds, details);
};

export const selectPickemMatchIdsFromSwiss = (
  state: RootState,
  swiss: Swiss,
  details: PickemDetails
) => {
  const matchIds = swiss.rounds.flatMap((r) => r.matchIds);
  return selectPickemMatchIds(state, matchIds, details);
};

export const selectPickemCloseTime = (
  state: RootState,
  segment: TournamentSegment,
  details: PickemDetails
) => {
  const matchIds = selectPickemMatchIdsFromSegment(state, segment, details);
  if (matchIds.length === 0) {
    return undefined;
  }

  const startTimes = nonUndefined(
    matchIds.map((id) => selectMatchStartTime(state, id))
  );

  if (startTimes.length === 0) {
    return undefined;
  }

  return Math.min(...startTimes);
};

export const selectPickemOpenAndCloseTime = (
  state: RootState,
  details: PickemDetails
) => {
  if (details.closeTimeMsOverride) {
    const closeTime = details.closeTimeMsOverride;
    const openTime = !!details.openTimeMsOverride
      ? details.openTimeMsOverride
      : closeTime - PICKEM_OPEN_THRESHOLD_MS;
    return { openTime, closeTime };
  }

  const segment = selectTournamentSegment(
    state,
    details.tournamentId,
    details.segmentId
  );
  if (!segment) {
    return undefined;
  }

  const closeTime = selectPickemCloseTime(state, segment, details);

  if (!closeTime) {
    return undefined;
  }

  const openTime = !!details.openTimeMsOverride
    ? details.openTimeMsOverride
    : closeTime - PICKEM_OPEN_THRESHOLD_MS;
  return { openTime, closeTime };
};

export const selectIsActivePickem = (
  state: RootState,
  details: PickemDetails
) => {
  const timeMs = getTimeMs();
  const pickemTimes = selectPickemOpenAndCloseTime(state, details);
  if (!pickemTimes) {
    return false;
  }

  return pickemTimes.closeTime > timeMs && pickemTimes.openTime < timeMs;
};

export const selectActivePickemDetails = (state: RootState) => {
  const timeMs = getTimeMs();
  const allPickems = selectAllPickemDetails(state);
  const activePickems: {
    pickemId: string;
    tournamentId: string;
    name: string;
    closeTime: number;
  }[] = [];
  allPickems.forEach((p) => {
    const pickemTimes = selectPickemOpenAndCloseTime(state, p);
    if (
      !!pickemTimes &&
      numberInRange(timeMs, pickemTimes.openTime, pickemTimes.closeTime)
    ) {
      activePickems.push({
        pickemId: p.pickemId,
        tournamentId: p.tournamentId,
        name: p.name,
        closeTime: pickemTimes.closeTime,
      });
    }
  });

  return activePickems;
};

export const selectUpcomingPickemDetails = (
  state: RootState,
  threshold: number,
  minimum: number
) => {
  const timeMs = getTimeMs();
  const allPickems = selectAllPickemDetails(state);
  const upcomingPickems: {
    pickemId: string;
    tournamentId: string;
    name: string;
    openTime: number;
  }[] = [];

  allPickems.forEach((p) => {
    const pickemTimes = selectPickemOpenAndCloseTime(state, p);
    if (!!pickemTimes && pickemTimes.openTime > timeMs) {
      upcomingPickems.push({
        pickemId: p.pickemId,
        tournamentId: p.tournamentId,
        name: p.name,
        openTime: pickemTimes.openTime,
      });
    }
  });
  const upcoming = upcomingPickems.sort((a, b) => a.openTime - b.openTime);
  const upcomingWithinThreshold = upcoming.filter(
    (u) => u.openTime < getTimeMs() + threshold
  );

  if (upcomingWithinThreshold.length < minimum) {
    const startIdx = upcomingWithinThreshold.length;
    const endIdx = Math.min(upcoming.length, minimum);
    return upcomingWithinThreshold.concat(upcoming.slice(startIdx, endIdx));
  }

  return upcomingWithinThreshold;
};

export const selectClosedTournamentPickemDetails = (
  state: RootState,
  tournamentId: string,
  isAuthenticatedUser: boolean
) => {
  const timeMs = getTimeMs();
  const tournamentPickemDetails = selectTournamentPickemDetails(
    state,
    tournamentId
  );
  if (!tournamentPickemDetails) {
    return undefined;
  }

  const closedPickemDetails: PickemDetails[] = [];
  tournamentPickemDetails.pickems.forEach((p) => {
    const closeTime = selectPickemOpenAndCloseTime(state, p)?.closeTime;
    if (isAuthenticatedUser || !closeTime || closeTime < timeMs) {
      closedPickemDetails.push(p);
    }
  });

  return closedPickemDetails;
};

export const selectTournamentIdsWithPickem = (
  state: RootState,
  year: string
) => {
  const tournamentIds = selectTournamentsByYear(state, year).map((t) => t.id);
  return selectAllPickemDetails(state)
    .filter((p) => tournamentIds.includes(p.tournamentId))
    .map((p) => p.tournamentId);
};
